export const DEFAULT_SESSION_MODE = "conversation" as const;

export const ACTIVE_SESSION_MODES = ["conversation", "stealth", "zen", "journey", "maths"] as const;
export const DORMANT_SESSION_MODES = ["visual"] as const;

export type ActiveSessionMode = (typeof ACTIVE_SESSION_MODES)[number];
export type DormantSessionMode = (typeof DORMANT_SESSION_MODES)[number];
export type SessionMode = ActiveSessionMode | DormantSessionMode;

export const DEFAULT_DISPATCH_AGENT_NAME = "speaking-practice-pro-agent";
export const DORMANT_DISPATCH_AGENT_NAME = "canvas-exercise-agent";

const ACTIVE_SESSION_MODE_SET = new Set<SessionMode>(ACTIVE_SESSION_MODES);

export function parseSessionMode(rawMode: string | null | undefined): SessionMode | null {
  if (
    rawMode === "conversation" ||
    rawMode === "stealth" ||
    rawMode === "zen" ||
    rawMode === "journey" ||
    rawMode === "maths"
  ) {
    return rawMode;
  }

  if (rawMode === "visual") {
    return rawMode;
  }

  return null;
}

export function normalizeSessionMode(rawMode: string | null | undefined): SessionMode {
  return parseSessionMode(rawMode) ?? DEFAULT_SESSION_MODE;
}

export function isSupportedSessionMode(mode: SessionMode): mode is ActiveSessionMode {
  return ACTIVE_SESSION_MODE_SET.has(mode);
}

export function getSupportedSessionMode(mode: string | null | undefined): ActiveSessionMode {
  if (mode === null || mode === undefined || mode === "") {
    return DEFAULT_SESSION_MODE;
  }

  const parsed = parseSessionMode(mode);
  if (parsed && isSupportedSessionMode(parsed)) {
    return parsed;
  }

  throw new UnsupportedSessionModeError(mode);
}

export function getDispatchAgentName(mode: ActiveSessionMode): string {
  switch (mode) {
    case "conversation":
    case "stealth":
    case "zen":
    case "journey":
    case "maths":
      return DEFAULT_DISPATCH_AGENT_NAME;
  }

  return DEFAULT_DISPATCH_AGENT_NAME;
}

export class UnsupportedSessionModeError extends Error {
  readonly mode: string;

  constructor(mode: string) {
    super(
      `Unsupported session mode "${mode}". Supported modes: ${ACTIVE_SESSION_MODES.join(", ")}.`
    );
    this.name = "UnsupportedSessionModeError";
    this.mode = mode;
  }
}
